import {
  ActionKey,
  ACTION_KEY_GROUPS,
  SIDEBAR_DEFINITION,
  getConfigurablePages,
} from '@pokerroom/types';
import type { FeatureKey, PagePermissionDto } from '@pokerroom/types';
import { buildNavItems } from '../../components/layout/navItems';

const navigation = buildNavItems((key) => key);
const pageOrder = new Map(
  navigation
    .flatMap((item) => [item, ...(item.children ?? [])])
    .filter((item) => item.path)
    .map((item, index) => [item.path!, index]),
);
const groupOrder = new Map(navigation.map((item, index) => [item.label, index]));
const rank = (value: number | undefined) => value ?? Number.MAX_SAFE_INTEGER;

export const ROLE_PAGE_GROUPS = SIDEBAR_DEFINITION.map((group) => ({
  ...group,
  ...(group.children
    ? {
        children: group.children.map((child) =>
          child.pagePath === '/admin/points-config'
            ? { ...child, labelKey: 'nav.memberSettingsPages' }
            : child,
        ),
      }
    : {}),
}));

export function getRolePageGroups(hasFeature?: (key: FeatureKey) => boolean) {
  const paths = new Set<string>(getConfigurablePages(hasFeature).map((page) => page.pagePath));
  return ROLE_PAGE_GROUPS.flatMap((entry) => {
    if (entry.pagePath) return paths.has(entry.pagePath) ? [entry] : [];
    const children = entry.children!.filter((child) => paths.has(child.pagePath));
    return children.length ? [{ ...entry, children }] : [];
  });
}

export function initialPagePermissions(permissions: PagePermissionDto[] = []) {
  return Object.fromEntries([
    ...getConfigurablePages().map((page) => [page.pagePath, false] as const),
    ...permissions.map((page) => [page.pagePath, page.isVisible] as const),
  ]);
}

export function countPagePermissions(
  permissions: PagePermissionDto[],
  hasFeature?: (key: FeatureKey) => boolean,
) {
  const paths = new Set<string>(getConfigurablePages(hasFeature).map((page) => page.pagePath));
  return {
    visible: new Set(
      permissions
        .filter((page) => page.isVisible && paths.has(page.pagePath))
        .map((page) => page.pagePath),
    ).size,
    total: paths.size,
  };
}

// Display domains only: authorization keys and server contracts remain canonical.
export const ROLE_ACTION_GROUPS = [
  {
    labelKey: 'permissionDomain_member',
    actions: [
      ActionKey.MEMBER_READ,
      ActionKey.BRAVE_RANKING_READ,
      ActionKey.BRAVE_RANKING_MANAGE,
      ActionKey.MEMBER_REFERRAL_READ,
      ActionKey.MEMBER_REFERRAL_MANAGE,
      ActionKey.MEMBER_REFERRAL_RULES_MANAGE,
      ActionKey.MEMBER_NOTIFICATIONS_MANAGE,
      ActionKey.MEMBER_NOTIFICATIONS_SEND,
      ActionKey.RESERVATION_READ,
      ActionKey.RESERVATION_MANAGE,
      ActionKey.RESERVATION_CHECKIN,
      ActionKey.MEMBER_ACTIVITIES_IMPORT,
      ActionKey.MEMBER_ACTIVITIES_PUBLISH,
      ActionKey.MEMBER_TAGS_MANAGE,
      ActionKey.MEMBER_TAG_RULES_MANAGE,
      ActionKey.MEMBER_TAG_RULES_RUN,
      ActionKey.MEMBER_TAGS_ASSIGN,
      ActionKey.MEMBER_TAGS_ASSIGN_GLOBAL,
      ActionKey.MEMBER_TAGS_IMPORT,
      ActionKey.MEMBER_TAGS_EXPORT,
      ActionKey.MEMBER_PORTAL_MANAGE,
      ActionKey.MEMBER_LINE_REBIND,
      ActionKey.MEMBER_LINE_UNLINK,
      ActionKey.MEMBER_CREATE,
      ActionKey.MEMBER_UPDATE,
      ActionKey.MEMBER_DELETE,
      ActionKey.MEMBER_CHECKIN,
      ActionKey.MEMBER_LEVEL_MANAGE,
      ActionKey.MEMBER_FIELD_MANAGE,
      ActionKey.MEMBER_BULK_IMPORT,
      ActionKey.MEMBER_VIEW_NATIONAL_ID,
      ActionKey.MEMBER_ASSET_TYPE_MANAGE,
    ],
  },
  {
    labelKey: 'permissionDomain_accounting',
    actions: [
      ActionKey.MEMBER_REFERRAL_RECONCILE,
      ActionKey.MEMBER_ADJUST_POINTS,
      ActionKey.MEMBER_WALLET_MANAGE,
      ActionKey.MEMBER_ZIPA_OVERRIDE_NEGATIVE,
      ActionKey.MEMBER_ZIPA_TRANSFER,
      ActionKey.MEMBER_TIMED_SETTLEMENT_CONFIRM,
      ActionKey.MEMBER_CHARGE_READ,
      ActionKey.MEMBER_CHARGE_CONFIRM,
      ActionKey.MEMBER_CHARGE_REFUND,
      ActionKey.MEMBER_DEFERRED_ADMISSION_APPROVE,
      ActionKey.MEMBER_DEFERRED_PAYMENT_COLLECT,
      ActionKey.MEMBER_MEMBERSHIP_FEE_CONFIRM,
      ActionKey.ACCOUNTING_READ,
      ActionKey.ACCOUNTING_OPEN,
      ActionKey.ACCOUNTING_CLOSE,
      ActionKey.ACCOUNTING_EDIT,
      ActionKey.ACCOUNTING_CATEGORY_MANAGE,
      ActionKey.PAYOUT_APPROVE,
      ActionKey.DASHBOARD_VIEW_KPI,
    ],
  },
  {
    labelKey: 'permissionDomain_tournament',
    actions: [
      ActionKey.TOURNAMENT_CREATE,
      ActionKey.TOURNAMENT_UPDATE,
      ActionKey.TOURNAMENT_DELETE,
      ActionKey.TOURNAMENT_START,
      ActionKey.TOURNAMENT_PAUSE,
      ActionKey.TIMER_CONTROL,
      ActionKey.PLAYER_ELIMINATE,
      ActionKey.PLAYER_REBUY,
      ActionKey.PLAYER_ADDON,
      ActionKey.TOURNAMENT_UNDO,
      ActionKey.TOURNAMENT_EMERGENCY,
      ActionKey.TOURNAMENT_MANUAL_FIX,
      ActionKey.TOURNAMENT_REOPEN,
      ActionKey.BLIND_STRUCTURE_CREATE,
      ActionKey.BLIND_STRUCTURE_UPDATE,
      ActionKey.BLIND_STRUCTURE_DELETE,
      ActionKey.TEMPLATE_CREATE,
      ActionKey.TEMPLATE_UPDATE,
      ActionKey.TEMPLATE_DELETE,
      ActionKey.TOURNAMENT_CANCEL,
      ActionKey.TOURNAMENT_COMPLETE,
      ActionKey.TOURNAMENT_RESUME,
      ActionKey.DISPLAY_MANAGE,
      ActionKey.DISPLAY_TIMER_LAYOUT,
      ActionKey.VENUE_TABLE_MANAGE,
      ActionKey.VENUE_SEAT_MANAGE,
      ActionKey.VOICE_USE,
    ],
  },
  {
    labelKey: 'permissionDomain_governance',
    actions: [
      ActionKey.USER_CREATE,
      ActionKey.USER_UPDATE,
      ActionKey.USER_DELETE,
      ActionKey.USER_RESET_PASSWORD,
      ActionKey.USER_MANAGE_2FA,
      ActionKey.USER_GRANT_VENUE_SCOPE,
      ActionKey.DEVICE_BINDING_MANAGE,
      ActionKey.ROLE_CREATE,
      ActionKey.ROLE_UPDATE,
      ActionKey.ROLE_DELETE,
      ActionKey.ROLE_ASSIGN,
      ActionKey.BRANDING_MANAGE,
      ActionKey.I18N_MANAGE,
      ActionKey.SYSTEM_SETTINGS,
      ActionKey.LOG_DELETE,
    ],
  },
].map((domain) => ({
  ...domain,
  subgroups: ACTION_KEY_GROUPS.flatMap((group) => {
    const actions = group.actions.filter((action) => domain.actions.includes(action));
    return actions.length
      ? [
          {
            labelKey:
              domain.labelKey === 'permissionDomain_accounting' &&
              group.labelKey === 'actionGroup_member'
                ? 'permissionMemberFinance'
                : group.labelKey,
            actions,
          },
        ]
      : [];
  }),
}));

const PAGE_DOMAINS: Record<string, string> = {
  '/': 'permissionDomain_accounting',
  '/tables': 'permissionDomain_tournament',
  '/members': 'permissionDomain_member',
  '/members/brave-ranking': 'permissionDomain_member',
  '/admin/association-promotion': 'permissionDomain_member',
  '/check-in': 'permissionDomain_member',
  '/members/import': 'permissionDomain_member',
  '/accounting/open': 'permissionDomain_accounting',
  '/accounting/expenses': 'permissionDomain_accounting',
  '/accounting/settle': 'permissionDomain_accounting',
  '/accounting/report': 'permissionDomain_accounting',
  '/tournament': 'permissionDomain_tournament',
  '/tournament/create': 'permissionDomain_tournament',
  '/tournament/calendar': 'permissionDomain_tournament',
  '/tournament/blind-structures': 'permissionDomain_tournament',
  '/tournament/timed-settlement-levels': 'permissionDomain_tournament',
  '/tournament/timed-fee-plans': 'permissionDomain_tournament',
  '/tournament/templates': 'permissionDomain_tournament',
  '/tournament/payout-rules': 'permissionDomain_tournament',
  '/display': 'permissionDomain_tournament',
  '/admin/users': 'permissionDomain_governance',
  '/admin/roles': 'permissionDomain_governance',
  '/admin/logs': 'permissionDomain_governance',
  '/admin/terminology': 'permissionDomain_governance',
  '/admin/email-settings': 'permissionDomain_governance',
  '/admin/points-config': 'permissionDomain_member',
  '/admin/venue': 'permissionDomain_governance',
  '/admin/timer-preview': 'permissionDomain_tournament',
  '/admin/branding': 'permissionDomain_governance',
  '/admin/member-portal-editor': 'permissionDomain_member',
  '/admin/venues': 'permissionDomain_governance',
  '/admin/page-settings': 'permissionDomain_governance',
  '/admin/tv-app': 'permissionDomain_tournament',
  '/admin/dev-tools': 'permissionDomain_governance',
};

export function getRolePageDomains(hasFeature?: (key: FeatureKey) => boolean) {
  const pages = getConfigurablePages(hasFeature);
  return ROLE_ACTION_GROUPS.map((group) => ({
    labelKey: group.labelKey,
    pages: pages.filter((page) => PAGE_DOMAINS[page.pagePath] === group.labelKey),
  }));
}

export function setVisiblePermissions(
  previous: Record<string, boolean>,
  visible: readonly string[],
  allowed: boolean,
) {
  return { ...previous, ...Object.fromEntries(visible.map((key) => [key, allowed])) };
}

export function permissionChanges(
  previous: Record<string, boolean>,
  next: Record<string, boolean>,
) {
  return [...new Set([...Object.keys(previous), ...Object.keys(next)])]
    .filter((key) => !!previous[key] !== !!next[key])
    .map((key) => ({ key, allowed: !!next[key] }));
}

export function permissionRiskKey(action: string) {
  const specific: Record<string, string> = {
    'member:charge_refund': 'permissionRiskRefund',
    'member:adjust_points': 'permissionRiskPoints',
    'member:zipa_override_negative': 'permissionRiskNegative',
    'member:wallet_manage': 'permissionRiskWallet',
    'tournament:reopen': 'permissionRiskReopen',
    'user:grant_venue_scope': 'permissionRiskScope',
  };
  return (
    specific[action] ??
    ([
      'user:create',
      'user:update',
      'user:delete',
      'user:reset_password',
      'user:manage_2fa',
      'user:grant_venue_scope',
      'device_binding:manage',
      'role:create',
      'role:update',
      'role:delete',
      'role:assign',
      'tournament:undo',
      'tournament:emergency',
      'tournament:manual_fix',
      'tournament:reopen',
      'member:referral_reconcile',
      'member:tags_assign_global',
      'member:tags_export',
      'member:delete',
      'member:bulk_import',
      'member:adjust_points',
      'member:view_national_id',
      'member:wallet_manage',
      'member:zipa_override_negative',
      'member:zipa_transfer',
      'member:charge_read',
      'member:charge_confirm',
      'member:charge_refund',
      'member:deferred_admission_approve',
      'member:deferred_payment_collect',
      'member:membership_fee_confirm',
      'accounting:close',
      'accounting:edit',
      'accounting:category_manage',
      'payout:approve',
      'system:settings',
      'log:delete',
    ].includes(action)
      ? 'permissionRiskReview'
      : null)
  );
}

export function orderPagePermissions<T extends { pagePath: string }>(permissions: T[]): T[] {
  return [...permissions].sort(
    (a, b) => rank(pageOrder.get(a.pagePath)) - rank(pageOrder.get(b.pagePath)),
  );
}

export function actionPermissionLabelKey(action: string): string {
  return `action_${action.replace(/:/g, '_')}`;
}
